import Loading from "components/core/blocks/Loading";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import { useState } from "react";
import { Row, Col, Nav, Badge } from "react-bootstrap";
import { Card } from "react-bootstrap/esm";
import ProductImage from "components/custom/category/listings/ProductImage";
import { INVENTORY_STATES, SALE_STATES } from "components/core/blocks/AdPlan/constants";
import PriceHistorySection from "./cards/PriceHistory";
import ImpactSection from "./cards/Impact";
import { DemandChartsSection } from "./cards/DemandTrend";
import Overview from "./cards/Overview";
import CompetitorsSection from "./cards/Competitors";
import ConfirmationSection from "./cards/Confirmation";
import moment from "moment";
import { Link } from "react-router-dom";
import BuyBoxOffers from "modules/pricingv2/cards/BuyBoxOffers";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { z } from "zod";
import { isValidDataframeDate } from "../../utils/formatText";
import TrellisNavLink from "components/core/basic/NavLink";
import { useAppSelector } from "../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";

interface Product {
  product_title: string;
  asin: string;
  sku: string;
  sales_state: string;
  stock_status: string;
}

// There's a _lot_ returned from this endpoint. Only typing out fields used in this component, expand as needed.
interface PricingProduct {
  confidence: string;
  enroll_date: string;
  enroll_eligibility: string;
  configured_slider_value: number;
  price_profiling: boolean;
  price_override_end: string;
  price_override: number;
  plan_id: number;
}

interface Props {
  productId: string;
  additionalBadges?: { text: string; color?: string }[];
}

export const ProductCard = ({ productId, additionalBadges = [] }: Props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { start, end, preStart, preEnd, group } = useDates();
  const hasBuyBoxModule = useModuleEnabled("BUYBOX");

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "product_id",
  };
  const { isLoading, data: product } = useFetch<Product>(
    ["products", productId, group, start, end, preStart, preEnd],
    `/api/gvads/productid/${productId}`,
    {},
    { select: (res) => res.data, placeholderData: keepPreviousData },
  );

  const { data, isLoading: isOverviewDataLoading } = useFetch(
    ["product_report"],
    `/api/data_report/product_report?product_id=${productId}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );
  const salesStateArray = [SALE_STATES.BEST_SELLER!, SALE_STATES.CLEARANCE!, SALE_STATES.NEW_PRODUCT!];
  const inventoryStateArray = [
    INVENTORY_STATES.OVERSTOCK!,
    INVENTORY_STATES.LOW_STOCK!,
    INVENTORY_STATES.OUT_OF_STOCK!,
  ];
  const salesLabel = salesStateArray.find((s) => s.value === product?.sales_state)?.label;
  const inventoryBadgeText = inventoryStateArray.find((s) => s.value === product?.stock_status)?.label;
  const [tab, setTab] = useState("overview");

  const { data: pricingProduct, isLoading: isPricingProductDataLoading } = useFetch<PricingProduct>(
    ["product_pricing", productId],
    `/pricing/products/${productId}/`,
    { get_primary: "false" },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  return (
    <>
      {isLoading || isPricingProductDataLoading || isOverviewDataLoading || !product ?
        <Loading fullPage={false} height={"300px"} />
      : <Card className="fs-standard mb-0">
          <Card.Body>
            <Row>
              <Col xs={12} xl={8}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "120px", paddingRight: "20px" }}>
                    <ProductImage id={productId} style={{ borderRadius: 15 }} width={100} height={100} />
                  </div>
                  <div>
                    <Row>
                      <Col xs={12}>
                        <h3
                          style={{
                            marginBottom: "0.2rem",
                            marginTop: "0",
                            fontSize: 22,
                          }}
                        >
                          {product.product_title}
                        </h3>
                      </Col>
                      <Col xs={12}>
                        {product.asin} - {product.sku}
                        &nbsp;-&nbsp;
                        <Badge
                          className={`badge bg-trellis-${pricingProduct?.enroll_date ? "green" : "lightgrey"} text-end`}
                          pill
                        >
                          {pricingProduct?.enroll_date && <span>Enrolled</span>}
                          {!pricingProduct?.enroll_date && !pricingProduct?.price_override_end && (
                            <span>Not Enrolled</span>
                          )}
                        </Badge>
                        {isValidDataframeDate(pricingProduct?.price_override_end) &&
                          moment().isBefore(pricingProduct.price_override_end) && (
                            <Badge className={`badge bg-trellis-purple text-end ms-3`} pill>
                              Fixed Price
                            </Badge>
                          )}
                      </Col>
                      <Col xs={12}>
                        {inventoryBadgeText && (
                          <Badge
                            className="badge bg-trellis-purple"
                            style={{
                              marginRight: "0.5rem",
                              backgroundColor: "#c43066",
                            }}
                            pill
                          >
                            {inventoryBadgeText}
                          </Badge>
                        )}
                        {salesLabel && (
                          <Badge className="bg-trellis-fuchsia" pill>
                            {salesLabel}
                          </Badge>
                        )}
                        {additionalBadges.map((badge, index) => (
                          <Badge
                            className="badge bg-trellis-mauve"
                            style={{
                              marginLeft: "0.5rem",
                              backgroundColor: badge.color,
                            }}
                            pill
                            key={`${badge.text}-${index}`}
                          >
                            {badge.text}
                          </Badge>
                        ))}
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
              <Col className="text-center" xs={12} xl={4}>
                {(pricingProduct?.confidence || pricingProduct?.enroll_date) && (
                  <Row>
                    <Col>
                      <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3">
                        Enroll Date
                      </h6>
                      {z.string().datetime().safeParse(pricingProduct.enroll_date).success ?
                        <span>{moment(pricingProduct.enroll_date).format("MM/DD/YYYY")}</span>
                      : <span>n/a</span>}
                    </Col>
                    <Col>
                      <span>&nbsp;</span>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
            <Row>
              <Col className="mt-5 ph-4 mb-3 border-bottom" xs={12}>
                <Nav
                  variant="underline"
                  onSelect={(k) => {
                    setTab(k!);
                  }}
                  activeKey={tab}
                >
                  <TrellisNavLink eventKey="overview">Overview</TrellisNavLink>
                  <TrellisNavLink eventKey="price_history">Price History</TrellisNavLink>
                  <TrellisNavLink eventKey="ad_affect">Advertising Effectiveness</TrellisNavLink>
                  <TrellisNavLink eventKey="competitors">Competitors</TrellisNavLink>
                  <TrellisNavLink eventKey="impact" visible={pricingProduct?.enroll_date !== undefined}>
                    Impact
                  </TrellisNavLink>

                  <TrellisNavLink eventKey="confirmation" visible={pricingProduct?.enroll_date !== undefined}>
                    Price Changes
                  </TrellisNavLink>

                  <TrellisNavLink eventKey="buy_box" visible={hasBuyBoxModule}>
                    Buy Box Offers
                  </TrellisNavLink>
                </Nav>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                {tab === "overview" && (
                  <Overview
                    isPricingProductDataLoading={isPricingProductDataLoading}
                    marketPlace={marketPlace}
                    data={data}
                    isLoading={isOverviewDataLoading}
                  />
                )}
                {tab === "price_history" && (
                  <>
                    <Card>
                      <DemandChartsSection
                        pricingProduct={pricingProduct}
                        marketPlace={marketPlace}
                        product={product}
                        isPricingProductDataLoading={isPricingProductDataLoading}
                        isLoading={false}
                      />
                    </Card>
                    <hr />
                    <PriceHistorySection
                      pricingProduct={pricingProduct}
                      marketPlace={marketPlace}
                      isPricingProductDataLoading={isPricingProductDataLoading}
                      isLoading={false}
                    />
                  </>
                )}
                {tab === "ad_affect" && (
                  <PriceHistorySection
                    pricingProduct={pricingProduct}
                    marketPlace={marketPlace}
                    isPricingProductDataLoading={isPricingProductDataLoading}
                    isLoading={isOverviewDataLoading}
                    advertising={true}
                  />
                )}
                {tab === "impact" && <ImpactSection pricingProduct={pricingProduct} marketPlace={marketPlace} />}
                {tab === "confirmation" && (
                  <ConfirmationSection
                    pricingProduct={pricingProduct}
                    marketPlace={marketPlace}
                    pricingPlan={undefined}
                    date={undefined}
                  />
                )}
                {tab === "competitors" && <CompetitorsSection productId={productId} marketPlace={marketPlace} />}
                {tab === "buy_box" && <BuyBoxOffers asin={product.asin} />}
              </Col>
            </Row>
            {pricingProduct?.plan_id && (
              <Row>
                <Col className="text-center py-2" xs={12}>
                  <Link to={`/user/_pricing/plan/${pricingProduct.plan_id}`}>View Pricing Plan</Link>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      }
    </>
  );
};
